import React, { FC } from 'react';
import Chip from '@mui/material/Chip';
import Stack, { StackProps } from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { ICreateAbsenceRequestDTO, IEmployeeBalanceDTO } from 'src/api/dtos';
import { AbsenceDates } from 'src/features/absences/AbsenceDates';
import { AbsenceDetailsRow } from 'src/features/absences/AbsenceDetailsRow';
import { TableBody } from 'src/shared/components';
import { DATE_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { formatDuration } from 'src/shared/utils';
import { getDurationFormat } from 'src/store/user/user.selectors';

import { AbsenceBalanceIcon } from './AbsenceBalanceIcon';
import { AccrualBalanceBreakdownTable } from './AccrualBalanceBreakdownTable';

interface Props {
  absence: Partial<ICreateAbsenceRequestDTO>;
  balance: IEmployeeBalanceDTO;
  showAccrualBalanceInDays: boolean;
  isFutureBalanceCheckEnabled: boolean;
  isFutureBalanceViolationBlockEnabled: boolean;
  hideBreakdown?: boolean;
  sx?: StackProps['sx'];
  titleVariant?: 'sm' | 'md';
}

export const AbsenceBalanceDetailsSection: FC<Props> = ({
  absence,
  balance,
  showAccrualBalanceInDays,
  isFutureBalanceCheckEnabled,
  isFutureBalanceViolationBlockEnabled,
  hideBreakdown,
  sx,
  titleVariant = 'sm',
}) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const durationFormat = useSelector(getDurationFormat);

  const currentBalanceDays =
    showAccrualBalanceInDays && balance.currentBalanceInDays
      ? `/ ${balance.currentBalanceInDays} ${t('global.days')}`
      : undefined;

  const expectedBalanceDays =
    showAccrualBalanceInDays && balance.expectedBalanceInDays
      ? `/ ${balance.expectedBalanceInDays} ${t('global.days')}`
      : undefined;

  const hasEmptyBalance = !balance?.currentBalanceInSeconds && !balance?.expectedBalanceInSeconds;
  const showFutureBalance = !hasEmptyBalance && isFutureBalanceCheckEnabled;

  return (
    <Stack direction="column" spacing={4} sx={sx}>
      <Stack direction="column" spacing={2}>
        <Typography sx={{ fontSize: titleVariant === 'md' ? '1.25rem' : '1rem', fontWeight: 500 }}>
          {t('accrualSummary.balanceSummary')}
        </Typography>
        <Table>
          <TableBody>
            <AbsenceDetailsRow
              label={t('accrualSummary.absenceDates')}
              value={
                <AbsenceDates
                  startDate={absence.startDate as string}
                  endDate={absence.endDate || (absence.startDate as string)}
                  startTime={absence.startTime}
                  endTime={absence.endTime}
                  sx={{ fontSize: '0.875rem', color: theme.palette.text.primary }}
                />
              }
            />
            <AbsenceDetailsRow
              label={t('global.status')}
              value={
                <AbsenceBalanceIcon
                  size={28}
                  hideTooltip
                  balance={balance}
                  shouldShowError={isFutureBalanceViolationBlockEnabled}
                />
              }
            />
            {!!balance?.currentBalanceInSeconds && (
              <AbsenceDetailsRow
                label={t('accrualSummary.currentBalance')}
                value={
                  <Chip
                    variant="filled"
                    color="info"
                    sx={{
                      '&.MuiChip-root': { borderRadius: '0.5rem' },
                    }}
                    label={
                      <>
                        {formatDuration(balance.currentBalanceInSeconds, durationFormat, 'seconds')}{' '}
                        {currentBalanceDays}
                      </>
                    }
                  />
                }
              />
            )}
            {showFutureBalance && (
              <>
                <AbsenceDetailsRow
                  label={
                    <Stack direction="column">
                      <div>{t('accrualSummary.expectedBalance')}</div>
                      <Typography fontSize="0.75rem" color={theme.palette.text.primary}>
                        ({t('global.asOfDate', { date: moment(balance.date).format(DATE_FORMAT) })})
                      </Typography>
                    </Stack>
                  }
                  value={
                    <Chip
                      variant="filled"
                      sx={{
                        '&.MuiChip-root': {
                          backgroundColor: theme.palette.mode === 'dark' ? '#6c757d' : '#8F96AE',
                          color: '#fff',
                          borderRadius: '0.5rem',
                        },
                      }}
                      label={
                        <>
                          {formatDuration(balance.expectedBalanceInSeconds, durationFormat, 'seconds')}{' '}
                          {expectedBalanceDays}
                        </>
                      }
                    />
                  }
                />
              </>
            )}
          </TableBody>
        </Table>
      </Stack>

      {!hideBreakdown && (
        <Stack direction="column" spacing={2}>
          <Typography sx={{ fontSize: titleVariant === 'md' ? '1.25rem' : '1rem', fontWeight: 500 }}>
            {t('accrualSummary.balanceBreakdown')}
          </Typography>
          <AccrualBalanceBreakdownTable
            startDate={absence.startDate as string}
            endDate={absence.endDate ?? (absence.startDate as string)}
            startTime={absence.startTime}
            endTime={absence.endTime}
            transactions={balance.employeeBalancesTrans}
            status={balance.status}
          />
        </Stack>
      )}
    </Stack>
  );
};
